/**
 * What a prospect's Class reads as on a list.
 *
 * One column, two ladders. A JUCO player's class is a year of junior college — coaches say
 * "JC 2" out loud — while everyone else's class is the graduation year they have always shown.
 * `utils/roster-class` owns the vocabulary; this module owns the *choice* between the two, so
 * the web table, the mobile card and the mobile iPad table cannot drift apart on it.
 *
 * TWO TRAPS THIS MODULE EXISTS TO ABSORB:
 *
 *   1. `playerLevel = 0` is High School and is **falsy in JS**. Every check here goes through
 *      `isJucoPlayerLevel` / `formatPlayerLevel`, which test `!= null`. A truthiness check would
 *      read the most common level on a recruiting board as "no level set".
 *   2. A JUCO player whose `jucoYear` is null must still render something. Falling through to the
 *      graduation year would print a four-year-ladder inference for a two-year player, and
 *      printing nothing leaves a blank cell that reads as missing data rather than as a partly
 *      filled record. It renders the bare level instead.
 *
 * Mirrors `mobile/utils/prospect-class-display.ts`. Keep the two in step.
 */

import { formatJucoYear } from "./roster-class";
import { formatPlayerLevel, isJucoPlayerLevel } from "../constants/playerLevels";

/** What a JUCO player with no JC year on file shows. Never a blank cell. */
export const JUCO_CLASS_FALLBACK = "JUCO";

/** What a player with no class information at all shows — the table's long-standing dash. */
export const NO_CLASS = "-";

/** The only fields the decision reads. Both `ProspectResponse` and `ProspectSummaryResponse` fit. */
export interface ProspectClassFacts {
  gradYear?: number | null;
  jucoYear?: string | null;
  playerLevel?: number | null;
}

export interface ProspectClassDisplay {
  /** The string to render. Never empty — see `JUCO_CLASS_FALLBACK` and `NO_CLASS`. */
  text: string;
  /**
   * False only when there is no class information at all (`text` is `NO_CLASS`).
   *
   * The table cell renders the dash regardless; mobile's card omits its pill instead of showing
   * one that says nothing. Exposed so neither caller compares `text` against a sentinel.
   */
  hasValue: boolean;
  /**
   * True when `text` is a year of junior college rather than a graduation year. Callers that
   * style the two differently (the mobile pill) branch on this rather than sniffing the string.
   */
  isJuco: boolean;
  /**
   * "High School" | "JUCO" | "Transfer Portal", or null when the server sent no level.
   *
   * Surfaced so a list can name the level beside a value that does not state it: `2027` alone
   * does not say whether the player is in high school or in the transfer portal.
   */
  levelLabel: string | null;
  /**
   * What a screen reader should say instead of the bare abbreviation.
   *
   * "JC 2" is announced as loose characters, and "2027" does not say what kind of year it is.
   * The table cell carries this as `aria-label`, which replaces the cell's own text for assistive
   * technology while leaving the visible text (and the cell's `textContent`) untouched.
   */
  accessibilityLabel: string;
}

export function describeProspectClass(
  prospect: ProspectClassFacts,
): ProspectClassDisplay {
  const levelLabel = formatPlayerLevel(prospect.playerLevel);

  if (isJucoPlayerLevel(prospect.playerLevel)) {
    return {
      text: formatJucoYear(prospect.jucoYear) ?? JUCO_CLASS_FALLBACK,
      hasValue: true,
      isJuco: true,
      levelLabel,
      accessibilityLabel:
        prospect.jucoYear != null
          ? `Junior college year ${prospect.jucoYear}`
          : "Junior college, year not set",
    };
  }

  if (prospect.gradYear == null) {
    return {
      text: NO_CLASS,
      hasValue: false,
      isJuco: false,
      levelLabel,
      accessibilityLabel: "Class not set",
    };
  }

  return {
    text: String(prospect.gradYear),
    hasValue: true,
    isJuco: false,
    levelLabel,
    // The level qualifies the year when it is known: "2027" alone does not say whether the
    // player is in high school or in the transfer portal.
    accessibilityLabel:
      levelLabel != null
        ? `${levelLabel}, graduation year ${prospect.gradYear}`
        : `Graduation year ${prospect.gradYear}`,
  };
}
